import { useState, useEffect, useRef } from 'react';
import { createConsultation, updateConsultation, fetchProperties } from '../utils/api';
import ConditionFilterModal from './condition_filter_modal';

const RANGE_FIELDS = [
  { key: '층수', label: '층수' },
  { key: '총층수', label: '총층수' },
  { key: '평수', label: '평수' },
  { key: '보증금', label: '보증금' },
  { key: '월세', label: '월세' },
  { key: '관리비', label: '관리비' },
  { key: '권리비', label: '권리비' },
];

function ConsumerCard({ isOpen, onClose, onSuccess, existingProperties = [], initialData = null, isEditMode = false }) {
  const [customerName, setCustomerName] = useState('');
  const [consultations, setConsultations] = useState([
    {
      date: new Date().toISOString().split('T')[0], // 오늘 날짜
      conditions: '',
      interestedProperties: '',
      memo: ''
    }
  ]);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [currentFilterIndex, setCurrentFilterIndex] = useState(null);
  const [propertySearchTerms, setPropertySearchTerms] = useState({}); // { index: searchTerm }
  const [propertySearchResults, setPropertySearchResults] = useState({}); // { index: results }
  const [isPropertySearchOpen, setIsPropertySearchOpen] = useState({}); // { index: boolean }
  const [allProperties, setAllProperties] = useState([]);
  const consultationRefs = useRef({}); // 각 날짜별 ref 저장
  const formScrollRef = useRef(null); // 폼 스크롤 컨테이너 ref

  // 모달이 열릴 때 상태 초기화 또는 편집 데이터 로드
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && initialData) {
        // 편집 모드: 기존 데이터 로드
        setCustomerName(initialData.customerName || '');
        setConsultations(
          initialData.consultations && initialData.consultations.length > 0
            ? initialData.consultations.map(c => {
                let conditions = {};
                if (c.conditions) {
                  if (typeof c.conditions === 'object') {
                    conditions = c.conditions;
                  } else {
                    try {
                      conditions = JSON.parse(c.conditions);
                    } catch {
                      conditions = {};
                    }
                  }
                }
                
                let interestedProperties = [];
                if (c.interestedProperties) {
                  if (Array.isArray(c.interestedProperties)) {
                    interestedProperties = c.interestedProperties;
                  } else if (typeof c.interestedProperties === 'string') {
                    interestedProperties = c.interestedProperties.split(',').map(p => p.trim()).filter(Boolean);
                  }
                }
                
                return {
                  date: c.date || new Date().toISOString().split('T')[0],
                  conditions,
                  interestedProperties,
                  memo: c.memo || ''
                };
              })
              : [
                {
                  date: new Date().toISOString().split('T')[0],
                  conditions: {},
                  interestedProperties: [],
                  memo: ''
                }
              ]
        );
      } else {
        // 새로 작성 모드: 초기화
        setCustomerName('');
        setConsultations([
          {
            date: new Date().toISOString().split('T')[0],
            conditions: {},
            interestedProperties: [],
            memo: ''
          }
        ]);
      }
      setError(null);
      setPropertySearchTerms({});
      setPropertySearchResults({});
      setIsPropertySearchOpen({});
      setIsFilterModalOpen(false);
      setCurrentFilterIndex(null);
    }
  }, [isOpen, isEditMode, initialData]);

  // 매물 데이터 로드
  useEffect(() => {
    async function loadProperties() {
      try {
        const data = await fetchProperties();
        setAllProperties(data);
      } catch (error) {
        console.error('매물 데이터 로드 실패:', error);
      }
    }
    if (isOpen) {
      loadProperties();
    }
  }, [isOpen]);

  // 날짜 추가
  const handleAddDate = () => {
    const newIndex = consultations.length;
    setConsultations(prev => [
      ...prev,
      {
        date: new Date().toISOString().split('T')[0],
        conditions: {},
        interestedProperties: [],
        memo: ''
      }
    ]);
    // 새 날짜의 검색어 상태 초기화
    setPropertySearchTerms(prev => ({ ...prev, [newIndex]: '' }));
    
    // 새로 추가된 날짜 영역으로 스크롤
    setTimeout(() => {
      const newRef = consultationRefs.current[newIndex];
      if (newRef && formScrollRef.current) {
        newRef.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  // 날짜 삭제
  const handleRemoveDate = (index) => {
    if (consultations.length === 1) {
      setError('최소 하나의 상담 기록이 필요합니다.');
      return;
    }
    setConsultations(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  // 날짜별 필드 변경
  const handleConsultationChange = (index, field, value) => {
    setConsultations(prev => prev.map((consultation, i) => 
      i === index ? { ...consultation, [field]: value } : consultation
    ));
    setError(null);
  };

  // 조건 필터 모달 열기
  const handleOpenFilterModal = (index) => {
    setCurrentFilterIndex(index);
    setIsFilterModalOpen(true);
  };

  // 조건 필터 적용
  const handleFilterApply = (filters) => {
    if (currentFilterIndex !== null) {
      handleConsultationChange(currentFilterIndex, 'conditions', filters);
    }
    setIsFilterModalOpen(false);
    setCurrentFilterIndex(null);
  };

  // 필터를 태그 문자열로 변환
  const filtersToTags = (filters) => {
    if (!filters || typeof filters !== 'object') return [];
    const tags = [];
    
    // 다중 선택
    if (Array.isArray(filters.상권) && filters.상권.length > 0) {
      tags.push(...filters.상권.map(v => `상권: ${v}`));
    }
    if (Array.isArray(filters.용도) && filters.용도.length > 0) {
      tags.push(...filters.용도.map(v => `용도: ${v}`));
    }
    
    // 단일 선택
    if (filters.엘베) tags.push(`엘베: ${filters.엘베}`);
    if (filters.화장실) tags.push(`화장실: ${filters.화장실}`);
    
    // 관심 매물
    if (filters.관심매물손님) {
      tags.push(`관심 매물: ${filters.관심매물손님}`);
    }
    
    // 범위
    RANGE_FIELDS.forEach(field => {
      const range = filters[field.key];
      if (range && (range.min || range.max)) {
        const min = range.min ? range.min.toString().trim() : '';
        const max = range.max ? range.max.toString().trim() : '';
        
        if (min && max) {
          // 둘 다 있으면 범위로 표시
          tags.push(`${field.label}: ${min}~${max}${getUnit(field.key)}`);
        } else if (min) {
          // 최소값만 있으면
          tags.push(`${field.label}: ${min}${getUnit(field.key)} 이상`);
        } else if (max) {
          // 최대값만 있으면
          tags.push(`${field.label}: ${max}${getUnit(field.key)} 이하`);
        }
      }
    });
    
    // 단위 반환 함수
    function getUnit(key) {
      const units = {
        '층수': '층',
        '총층수': '층',
        '평수': '평',
        '보증금': '만원',
        '월세': '만원',
        '관리비': '만원',
        '권리비': '만원'
      };
      return units[key] || '';
    }
    
    return tags;
  };

  // 관심 매물 검색
  const handlePropertySearch = (index, searchTerm) => {
    setPropertySearchTerms(prev => ({ ...prev, [index]: searchTerm }));
    if (searchTerm.trim()) {
      const filtered = allProperties.filter(prop => 
        (prop.상호 || prop.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
      setPropertySearchResults(prev => ({ ...prev, [index]: filtered.slice(0, 10) }));
      setIsPropertySearchOpen(prev => ({ ...prev, [index]: true }));
    } else {
      setPropertySearchResults(prev => ({ ...prev, [index]: [] }));
      setIsPropertySearchOpen(prev => ({ ...prev, [index]: false }));
    }
  };

  // 관심 매물 입력 필드 포커스 해제 시
  const handlePropertySearchBlur = (index) => {
    // 약간의 지연을 두어 클릭 이벤트가 먼저 처리되도록
    setTimeout(() => {
      setIsPropertySearchOpen(prev => ({ ...prev, [index]: false }));
    }, 200);
  };

  // 관심 매물 추가
  const handleAddInterestedProperty = (index, property) => {
    const current = consultations[index].interestedProperties || [];
    const propertyName = property.상호 || property.name || '';
    if (!current.includes(propertyName)) {
      handleConsultationChange(index, 'interestedProperties', [...current, propertyName]);
    }
    setPropertySearchTerms(prev => ({ ...prev, [index]: '' }));
    setIsPropertySearchOpen(prev => ({ ...prev, [index]: false }));
  };

  // 관심 매물 삭제
  const handleRemoveInterestedProperty = (index, propertyName) => {
    const current = consultations[index].interestedProperties || [];
    handleConsultationChange(index, 'interestedProperties', current.filter(p => p !== propertyName));
  };

  // 제출
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // 필수 필드 검증
    if (!customerName.trim()) {
      setError('고객명을 입력해주세요.');
      return;
    }

    if (consultations.some(c => !c.date)) {
      setError('모든 상담 기록에 날짜를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);

    try {
      const consultationData = {
        customerName: customerName.trim(),
        consultations: consultations.map(c => ({
          date: c.date,
          conditions: typeof c.conditions === 'object' ? c.conditions : {},
          interestedProperties: Array.isArray(c.interestedProperties) ? c.interestedProperties : [],
          memo: c.memo.trim()
        }))
      };
      
      if (isEditMode && initialData?.id) {
        // 편집 모드: 업데이트
        // eslint-disable-next-line no-unused-vars
        const savedConsultation = await updateConsultation(initialData.id, consultationData);
      } else {
        // 새로 작성 모드: 생성
        // eslint-disable-next-line no-unused-vars
        const savedConsultation = await createConsultation(consultationData);
      }
      
      // 성공 시 콜백 호출
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('상담 저장 실패:', err);
      setError(err.message || '상담 저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-3xl max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col">
        {/* 헤더 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-sm md:text-xl font-semibold text-gray-800">
            {isEditMode ? '상담 일지 편집' : '새 상담 일지 작성'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 본문 */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" ref={formScrollRef}>
          <div className="p-4 md:p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* 고객명 */}
            <div>
              <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                고객명 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => {
                  setCustomerName(e.target.value);
                  setError(null);
                }}
                placeholder="고객명을 입력하세요"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
                required
              />
            </div>

            {/* 상담 기록 목록 */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-xs md:text-sm font-medium text-gray-700">
                  상담 기록
                </label>
                <button
                  type="button"
                  onClick={handleAddDate}
                  className="px-3 py-1.5 text-xs md:text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors"
                >
                  + 날짜 추가
                </button>
              </div>

              {consultations.map((consultation, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    consultationRefs.current[index] = el;
                  }}
                  className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50"
                >
                  {/* 날짜 헤더 */}
                  <div className="flex items-center justify-between pb-2 border-b border-gray-300">
                    <div className="flex items-center gap-2">
                      <label className="text-xs md:text-sm font-medium text-gray-700">
                        상담 날짜 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={consultation.date}
                        onChange={(e) => handleConsultationChange(index, 'date', e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
                        required
                      />
                    </div>
                    {consultations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveDate(index)}
                        className="text-red-500 hover:text-red-700 text-xs md:text-sm"
                      >
                        삭제
                      </button>
                    )}
                  </div>

                  {/* 조건 */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
                      조건
                    </label>
                    {(() => {
                      const conditionTags = filtersToTags(consultation.conditions);
                      return conditionTags.length > 0 ? (
                        <div 
                          onClick={() => handleOpenFilterModal(index)}
                          className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-md bg-white min-h-[60px] cursor-pointer hover:border-orange-400 transition-colors"
                        >
                          {conditionTags.map((tag, tagIdx) => (
                            <span
                              key={tagIdx}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // 태그 삭제는 필터 모달에서 다시 설정하도록 유도
                                  handleOpenFilterModal(index);
                                }}
                                className="text-orange-600 hover:text-orange-800"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div 
                          onClick={() => handleOpenFilterModal(index)}
                          className="p-3 border border-gray-300 rounded-md bg-gray-50 text-xs md:text-sm text-gray-500 min-h-[60px] flex items-center cursor-pointer hover:border-orange-400 hover:bg-gray-100 transition-colors"
                        >
                          조건을 선택하려면 클릭하세요
                        </div>
                      );
                    })()}
                  </div>

                  {/* 관심 매물 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      관심 매물
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={propertySearchTerms[index] || ''}
                        onChange={(e) => handlePropertySearch(index, e.target.value)}
                        onFocus={() => {
                          if ((propertySearchTerms[index] || '').trim()) {
                            setIsPropertySearchOpen(prev => ({ ...prev, [index]: true }));
                          }
                        }}
                        onBlur={() => handlePropertySearchBlur(index)}
                        placeholder="상호명으로 검색하세요"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-xs md:text-sm"
                      />
                      {/* 검색 결과 드롭다운 */}
                      {isPropertySearchOpen[index] && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                          {propertySearchResults[index] && propertySearchResults[index].length > 0 ? (
                            propertySearchResults[index].map((property) => (
                              <button
                                key={property.id}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault(); // blur 이벤트 방지
                                  handleAddInterestedProperty(index, property);
                                }}
                                className="w-full px-3 py-2 text-left hover:bg-orange-50 transition-colors border-b border-gray-100 last:border-b-0"
                              >
                                <div className="text-xs md:text-sm font-medium text-gray-800">{property.상호 || property.name}</div>
                                {property.주소 && (
                                  <div className="text-xs text-gray-500">{property.주소}</div>
                                )}
                              </button>
                            ))
                          ) : (propertySearchTerms[index] || '').trim() ? (
                            <div className="px-3 py-2 text-xs md:text-sm text-gray-500">검색 결과가 없습니다.</div>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {/* 선택된 관심 매물 태그 */}
                    {Array.isArray(consultation.interestedProperties) && consultation.interestedProperties.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {consultation.interestedProperties.map((propertyName, propIdx) => (
                          <span
                            key={propIdx}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md"
                          >
                            {propertyName}
                            <button
                              type="button"
                              onClick={() => handleRemoveInterestedProperty(index, propertyName)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 메모 */}
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                      메모
                    </label>
                    <textarea
                      value={consultation.memo}
                      onChange={(e) => handleConsultationChange(index, 'memo', e.target.value)}
                      placeholder="기타 메모사항을 입력하세요"
                      rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none text-xs md:text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </form>

        {/* 푸터 */}
        <div className="px-4 md:px-6 py-3 md:py-4 border-t border-gray-200 flex items-center justify-end gap-2 md:gap-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3 md:px-4 py-2 text-xs md:text-base text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            disabled={isSubmitting}
          >
            취소
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-3 md:px-4 py-2 text-xs md:text-base bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      {/* 조건 필터 모달 */}
      <ConditionFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => {
          setIsFilterModalOpen(false);
          setCurrentFilterIndex(null);
        }}
        onApply={handleFilterApply}
        initialFilters={currentFilterIndex !== null ? consultations[currentFilterIndex]?.conditions : {}}
      />
    </div>
  );
}

export default ConsumerCard;
